/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box, Button, Flex, Image, Stack, Text } from '@chakra-ui/react';

import { fetchAPI } from '@/api/fetch';
import { apiGetCandidateScreening, ScreeningResponse } from '@/api/services';
import {
  InterviewAgentStatus,
  InterviewAgentStep,
  useInterviewAgent
} from '@/store/InterviewAgent';
import { isScreeningCompleted, ScreeningQuestionVideoStatus, ScreeningStatus } from '@/types/enums';

import logo from '@/assets/icon/logo.svg';
import successIcon from '@/assets/icon/success.svg';
import warnIcon from '@/assets/icon/warn.svg';

import CompletedPage from './CompletedPage';
import DeviceTest from './DeviceTest';
import LoadingPage from './LoadingPage';
import Screening from './Screening';
import BeforeStartPage from './BeforeStartPage';
import Stepper from './Stepper';
import StartInterviewPage from './StartInterviewPage';
import QuestionStepper from './QuestionStepper';

export default function InterviewAgentIndex() {
  const [searchParams] = useSearchParams();
  const {
    accessToken: storedAccessToken,
    setAccessToken,
    setScreening,
    setCandidate,
    setJob,
    setStep,
    status,
    setStatus,
    step,
    setQuestions,
    setPracticeQuestions,
    setCurrentQuestionId,
    startCounterWithNumber,
    resumeCounter,
    reset
  } = useInterviewAgent();

  useEffect(() => {
    const fetchScreening = async () => {
      const accessToken = searchParams.get('access_token');
      if (!accessToken) {
        setStatus(InterviewAgentStatus.Error);
        return;
      }

      if (accessToken !== storedAccessToken) {
        reset();
      }

      try {
        const response = await fetchAPI<ScreeningResponse>({
          request: () => apiGetCandidateScreening(accessToken)
        });

        if (response.success) {
          const { screening, candidate, job, practice_questions } = response.data;

          if (
            storedAccessToken !== null &&
            accessToken === storedAccessToken &&
            status === InterviewAgentStatus.Started &&
            screening.status === ScreeningStatus.InProgress
          ) {
            return;
          }

          reset();
          setAccessToken(accessToken);
          setScreening(screening);
          setCandidate(candidate);
          if (job) {
            setJob(job);
          }

          if (isScreeningCompleted(screening.status)) {
            setStatus(InterviewAgentStatus.AlreadyCompleted);
            return;
          } else {
            setStatus(InterviewAgentStatus.Started);
          }

          if (screening.started_at) {
            // Calculate duration (in seconds) from screening.started_at to now
            const startedAt = new Date(screening.started_at * 1000).getTime();
            const now = Date.now();
            const durationSeconds = Math.floor((now - startedAt) / 1000);
            const timeLimit = screening.session_time_limit * 60;

            if (durationSeconds > timeLimit) {
              setStatus(InterviewAgentStatus.AlreadyCompleted);
            } else {
              startCounterWithNumber(timeLimit - durationSeconds);
            }
          }

          const practiceQuestions = practice_questions.map((question) => ({
            questionId: question.question_id,
            question: question.question,
            retakeCount: 0,
            completed: false,
            file: null
          }));
          setPracticeQuestions(practiceQuestions);

          let isInProgress = false;
          let nextQuestionId = '';
          const questions = screening.questions.map((question) => {
            const isCompleted = question.video_status !== ScreeningQuestionVideoStatus.NoVideo;
            if (isCompleted) {
              isInProgress = true;
            } else {
              if (!nextQuestionId) {
                nextQuestionId = question.question_id;
              }
            }

            return {
              questionId: question.question_id,
              question: question.question,
              retakeCount: 0,
              completed: isCompleted,
              file: null
            };
          });
          setQuestions(questions);
          if (isInProgress) {
            setStep(InterviewAgentStep.Screening);
            setCurrentQuestionId(nextQuestionId);
          } else {
            setCurrentQuestionId(practiceQuestions[0]?.questionId || '');
            setStep(InterviewAgentStep.Start);
          }
        } else {
          console.error(response.message);
          setStatus(InterviewAgentStatus.Error);
        }
      } catch (error) {
        console.error(error);
        setStatus(InterviewAgentStatus.Error);
      }
    };

    fetchScreening();
  }, [searchParams]);

  useEffect(() => {
    resumeCounter();
  }, [resumeCounter]);

  if (status === InterviewAgentStatus.Error) {
    return <ErrorPage />;
  } else if (status === InterviewAgentStatus.Loading) {
    return <LoadingPage />;
  } else if (status === InterviewAgentStatus.AlreadyCompleted) {
    return <AlreadyCompletedPage />;
  }

  return (
    <Flex h="100%" maxW="100dvw" minH="100dvh" gap="16px">
      <Stack w="100%">
        <Flex alignItems="center" gap="2" p="4">
          <Box display="flex" alignItems="center" justifyContent="center" overflow="hidden">
            <Image src={logo} objectFit={'cover'} w="100%" h="32px" />
          </Box>
        </Flex>
        <Flex w="100%" justifyContent="center" mt="8px">
          {step < InterviewAgentStep.Screening && <Stepper w="80%" maxW="1000px" />}
          {step === InterviewAgentStep.Screening && <QuestionStepper w="80%" maxW="1000px" />}
        </Flex>
        <Flex w="100%" flexGrow={1} mb="80px" alignItems="center" alignContent="center">
          {step === InterviewAgentStep.Start && <BeforeStartPage />}
          {step === InterviewAgentStep.DeviceTest && <DeviceTest />}
          {step === InterviewAgentStep.Practice && <Screening screeningType="practice" />}
          {step === InterviewAgentStep.StartInterview && <StartInterviewPage />}
          {step === InterviewAgentStep.Screening && <Screening screeningType="official" />}
          {step === InterviewAgentStep.Completed && <CompletedPage />}
        </Flex>
        <Flex
          w="100%"
          p="8px"
          position="fixed"
          bottom="0"
          left="0"
          bg="background.secondary"
          zIndex={10}
        >
          <Text color="neutrals.500" fontSize="12px" fontWeight={400} w="100%" textAlign="center">
            powered by{' '}
            <Text as="span" color="primary.900" fontWeight={700}>
              PolarisJobs
            </Text>
          </Text>
        </Flex>
      </Stack>
    </Flex>
  );
}
const ErrorPage = () => {
  return (
    <Flex h="100%" maxW="100dvw" minH="100dvh" alignItems="center" justifyContent="center">
      <Stack alignItems="center" gap="32px" maxW="480px" textAlign="center" p="24px">
        <Box
          w="120px"
          h="120px"
          borderRadius="50%"
          bg="error.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="4px solid"
          borderColor="error.300"
        >
          <Image src={warnIcon} w="60px" h="60px" />
        </Box>

        <Stack gap="16px">
          <Text fontSize="28px" fontWeight="bold" color="neutrals.900">
            Something went wrong
          </Text>
          <Text fontSize="16px" color="neutrals.500" lineHeight="1.6">
            We encountered an error while loading your interview. This might be due to an invalid or
            expired access link.
          </Text>
        </Stack>

        <Stack gap="12px" w="100%">
          <Button
            bg="primary.900"
            color="white"
            fontSize="16px"
            fontWeight="semibold"
            borderRadius="8px"
            py="12px"
            px="32px"
            _hover={{ bg: 'primary.700' }}
            _active={{ transform: 'scale(0.98)' }}
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
          <Text fontSize="14px" color="neutrals.400">
            If the problem persists, please contact your interviewer
          </Text>
        </Stack>
      </Stack>
    </Flex>
  );
};

const AlreadyCompletedPage = () => {
  return (
    <Flex h="100%" maxW="100dvw" minH="100dvh" alignItems="center" justifyContent="center">
      <Stack alignItems="center" gap="32px" maxW="480px" textAlign="center" p="24px">
        <Box
          w="120px"
          h="120px"
          borderRadius="50%"
          bg="success.100"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="4px solid"
          borderColor="success.300"
        >
          <Image src={successIcon} w="60px" h="60px" />
        </Box>

        <Stack gap="16px">
          <Text fontSize="28px" fontWeight="bold" color="neutrals.900">
            Interview Completed!
          </Text>
          <Text fontSize="16px" color="neutrals.500" lineHeight="1.6">
            You have already completed this screening interview. Thank you for your participation!
          </Text>
        </Stack>

        <Box
          bg="neutrals.50"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="20px"
          w="100%"
        >
          <Text fontSize="14px" color="neutrals.600" fontWeight="medium">
            What happens next?
          </Text>
          <Text fontSize="14px" color="neutrals.500" mt="8px">
            Your responses have been recorded and will be reviewed by the hiring team. You'll be
            contacted if you're selected for the next round.
          </Text>
        </Box>

        <Text fontSize="14px" color="neutrals.500">
          You can safely close this window
        </Text>
      </Stack>
    </Flex>
  );
};
